import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, del, errorMessage, get, patch, post } from '../../api/client';
import type { Media, NewsField, NewsItem, NewsLang, NewsTexts } from '../../api/types';
import { useMe } from '../../auth';
import { IconAlert, IconArrowLeft, IconExternal, IconTrash } from '../../components/icons';
import { Field, formatDate, Spinner, Switch, useFeedback } from '../../components/ui';
import { useDraft, useNavigationGuard, useUnloadGuard } from '../../hooks';
import { tk, useT } from '../../i18n';
import { SaveBar } from '../model/SaveBar';
import { PmPhoto } from './photo';
import { emptyTexts, FIELDS, LANGS, langState, LIMITS, newsDate, newsSlug, objectPosition } from './shared';

interface Form {
  slug: string;
  date: string;
  source: string;
  url: string;
  texts: NewsTexts;
  image: Media | null;
  crop: { x: number; y: number };
  visible: boolean;
}

const fromItem = (n: NewsItem | null): Form =>
  n
    ? { slug: n.slug, date: n.date, source: n.source, url: n.url, texts: n.texts, image: n.image, crop: n.crop, visible: n.visible }
    : { slug: '', date: new Date().toISOString().slice(0, 10), source: '', url: '', texts: emptyTexts(), image: null, crop: { x: 0.5, y: 0.5 }, visible: true };

/** Los textos se traducen con t() al mostrarlos. */
const FIELD_LABELS: Record<NewsField, { label: string; hint: string }> = {
  headline: { label: tk('Titular'), hint: tk('Corto: va en mayúsculas en el carrusel de la landing y como título de la página de la noticia.') },
  title: { label: tk('Título de la nota'), hint: tk('El título completo, tal como lo publicó el medio: va debajo del titular.') },
  intro: { label: tk('Resumen'), hint: tk('Dos o tres frases. En la landing se corta a tres líneas; en la página de la noticia se lee entero.') },
};

export function NewsEditor() {
  const { id = 'nueva' } = useParams();
  const isNew = id === 'nueva';
  const { data, isPending, error } = useQuery({
    queryKey: ['pm-news', id],
    queryFn: async () => (await get<{ news: NewsItem }>(`/admin/pm/news/${id}`)).news,
    enabled: !isNew,
  });
  if (!isNew && isPending) return <Spinner />;
  if (!isNew && (error || !data)) return <div className="notice error">{errorMessage(error)}</div>;
  return <NewsForm key={id} item={isNew ? null : data!} />;
}

function NewsForm({ item }: { item: NewsItem | null }) {
  const { t, tr } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { toast, confirm } = useFeedback();
  const initial = useMemo(() => fromItem(item), [item]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial);
  const [lang, setLang] = useState<NewsLang>('en');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  // Mientras no se toque a mano, el slug de una noticia nueva sale del titular en inglés.
  const [slugTouched, setSlugTouched] = useState(!!item);
  const [leaving, setLeaving] = useState(false);
  useUnloadGuard(dirty && !leaving);
  useNavigationGuard(dirty && !leaving);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setF((prev) => ({ ...prev, [key]: value }));
  const setText = (l: NewsLang, field: NewsField, value: string) =>
    setF((prev) => {
      const texts = { ...prev.texts, [l]: { ...prev.texts[l], [field]: value } };
      const auto = !slugTouched && l === 'en' && field === 'headline';
      return { ...prev, texts, ...(auto && { slug: newsSlug(value) }) };
    });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pm-news', 'list'] });
    void qc.invalidateQueries({ queryKey: ['pm-publish'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  const save = async () => {
    const submitted = f;
    if (!f.image) {
      setError(new ApiError(400, t('Falta la foto de la noticia')));
      return;
    }
    setBusy(true);
    setError(null);
    const body = {
      slug: f.slug.trim(),
      date: f.date,
      source: f.source,
      url: f.url.trim(),
      texts: f.texts,
      imageMediaId: f.image.id,
      crop: f.crop,
      visible: f.visible,
    };
    try {
      if (!item) {
        const { news } = await post<{ news: NewsItem }>('/admin/pm/news', body);
        qc.setQueryData(['pm-news', news.id], news);
        refresh();
        toast(t('Noticia creada. Se verá en el sitio después de publicar.'));
        // Sin la guarda de "cambios sin guardar": ya se guardó todo.
        flushSync(() => setLeaving(true));
        navigate(`/noticias/${news.id}`, { replace: true });
        return;
      }
      const { news } = await patch<{ news: NewsItem }>(`/admin/pm/news/${item.id}`, body);
      markSaved(submitted);
      qc.setQueryData(['pm-news', item.id], news);
      refresh();
      toast(t('Guardado. Se verá en el sitio después de publicar.'));
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, errorMessage(e));
      setError(err);
      // Si el error es de un texto, se abre la pestaña de su idioma.
      const bad = LANGS.find((l) => FIELDS.some((field) => err.field(`texts.${l.code}.${field}`)));
      if (bad) setLang(bad.code);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!item) return;
    const ok = await confirm(
      tr('¿Borrar la noticia {title}? Sale del sitio en la próxima publicación. Si solo quieres sacarla un tiempo, ocúltala.', {
        title: <strong>{t('«{name}»', { name: item.texts.en.headline })}</strong>,
      }),
      { confirmLabel: t('Borrar'), danger: true },
    );
    if (!ok) return;
    try {
      await del(`/admin/pm/news/${item.id}`);
      toast(t('Noticia borrada'));
      refresh();
      flushSync(() => setLeaving(true));
      navigate('/noticias', { replace: true });
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  const monthOnly = /^\d{4}-\d{2}$/.test(f.date);
  const errors = item?.issues.filter((i) => i.level === 'error') ?? [];
  const warnings = item?.issues.filter((i) => i.level === 'warning') ?? [];
  const en = f.texts.en;
  const textError = (l: NewsLang, field: NewsField) => error?.field(`texts.${l}.${field}`);

  return (
    <>
      <Link to="/noticias" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }}>
        <IconArrowLeft /> {t('Noticias')}
      </Link>
      <header className="page-head">
        <div>
          <p className="kicker">{t('Noticia · {path}', { path: item ? `/news/${item.slug}/` : t('nueva') })}</p>
          <h1>{en.headline || (item ? item.slug : t('Nueva noticia'))}</h1>
          {item && (
            <p className="faint" style={{ fontSize: 13 }}>
              {t('Última edición: {date}', { date: formatDate(item.updatedAt) })}
            </p>
          )}
        </div>
        <div className="actions">
          <Switch checked={f.visible} onChange={(v) => set('visible', v)} label={f.visible ? t('Visible en el sitio') : t('Oculta')} />
          {item && me?.role === 'ADMIN' && (
            <button className="btn btn-danger" onClick={() => void remove()}>
              <IconTrash /> {t('Borrar')}
            </button>
          )}
        </div>
      </header>

      {(errors.length > 0 || warnings.length > 0) && (
        <div className={`notice ${errors.length ? 'error' : 'warn'}`} style={{ marginBottom: 18, display: 'block' }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>
            {errors.length ? t('Falta completar para poder publicarla visible') : t('Avisos (no impiden publicar)')}
          </strong>
          <ul className="issues">
            {[...errors, ...warnings].map((i) => (
              <li key={i.message}>
                <IconAlert className="ico" style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--warn)' }} />
                <span>{i.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && !error.errors.length && <div className="notice error" style={{ marginBottom: 18 }}>{error.message}</div>}

      <div className="news-editor">
        <section className="panel panel-pad stack">
          <div>
            <h2 className="section-title">{t('Foto y encuadre')}</h2>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {t('Arrastra el recuadro para elegir qué parte de la foto se ve. La imagen para compartir en redes es la misma, recortada arriba y abajo (la línea punteada).')}
            </p>
          </div>
          <PmPhoto
            media={f.image}
            crop={f.crop}
            onMedia={(m) => setF((prev) => ({ ...prev, image: m, crop: { x: 0.5, y: 0.5 } }))}
            onCrop={(c) => set('crop', c)}
            error={error?.field('imageMediaId')}
            ogGuide
            previews={(media, crop) => <NewsPreviews media={media} crop={crop} />}
          />
        </section>

        <section className="panel panel-pad stack">
          <h2 className="section-title">{t('La nota')}</h2>
          <div className="grid grid-2">
            <Field label={t('Medio')} error={error?.field('source')} count={{ value: f.source, max: LIMITS.source }} hint={t('Quién la publicó (p. ej. Visordown)')}>
              {(id) => <input id={id} className="input" value={f.source} onChange={(e) => set('source', e.target.value)} />}
            </Field>
            <Field
              label={monthOnly ? t('Mes') : t('Fecha')}
              error={error?.field('date')}
              hint={
                <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={monthOnly}
                    onChange={(e) => set('date', e.target.checked ? f.date.slice(0, 7) : `${f.date.slice(0, 7)}-01`)}
                  />
                  {t('Solo se sabe el mes ({date})', { date: newsDate(f.date) })}
                </label>
              }
            >
              {(id) => (
                <input
                  id={id}
                  className="input"
                  type={monthOnly ? 'month' : 'date'}
                  value={f.date}
                  onChange={(e) => e.target.value && set('date', e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field
            label={t('Enlace a la nota original')}
            error={error?.field('url')}
            hint={
              /^https?:\/\/\S+\.\S+/.test(f.url) ? (
                <a href={f.url} target="_blank" rel="noreferrer">
                  {t('Abrir la nota')} <IconExternal width={12} style={{ verticalAlign: '-1px' }} />
                </a>
              ) : (
                t('El botón «Read the full story» lleva aquí (se abre en otra pestaña)')
              )
            }
          >
            {(id) => <input id={id} className="input" type="url" placeholder="https://…" value={f.url} onChange={(e) => set('url', e.target.value)} />}
          </Field>
          <Field
            label={t('Dirección en el sitio')}
            error={error?.field('slug')}
            hint={
              item && f.slug !== item.slug
                ? t('Ojo: cambiarla cambia la dirección publicada y los enlaces viejos dejan de funcionar.')
                : t('Igual en los seis idiomas (/news/…, /es/noticias/…). Sale del titular en inglés.')
            }
          >
            {(id) => (
              <div className="input-affix input-prefix">
                <span className="affix">/news/</span>
                <input
                  id={id}
                  className="input mono"
                  value={f.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set('slug', e.target.value.toLowerCase());
                  }}
                />
                <span className="affix">/</span>
              </div>
            )}
          </Field>
        </section>

        <section className="panel panel-pad news-texts">
          <div className="row between row-wrap" style={{ marginBottom: 6 }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              {t('Textos')}
            </h2>
            <span className="faint" style={{ fontSize: 13 }}>
              {t('El inglés es obligatorio. Lo que quede vacío en otro idioma sale en inglés en el sitio.')}
            </span>
          </div>
          <div className="tabs lang-tabs" role="tablist">
            {LANGS.map((l) => {
              const state = langState(f.texts, l.code);
              const bad = FIELDS.some((field) => textError(l.code, field));
              return (
                <button key={l.code} role="tab" className="tab" aria-selected={lang === l.code} onClick={() => setLang(l.code)}>
                  <span className={`lang-dot ${bad ? 'bad' : state}`} aria-hidden="true" />
                  {t(l.name)}
                </button>
              );
            })}
          </div>
          <div className="stack">
            {FIELDS.map((field) => {
              const value = f.texts[lang][field];
              const fallback = lang !== 'en' ? f.texts.en[field] : '';
              const props = {
                className: `${field === 'intro' ? 'textarea' : 'input'} ${textError(lang, field) ? 'invalid' : ''}`,
                value,
                placeholder: fallback ? t('En inglés: {text}', { text: fallback }) : undefined,
                onChange: (e: { target: { value: string } }) => setText(lang, field, e.target.value),
              };
              return (
                <Field
                  key={`${lang}-${field}`}
                  label={lang === 'en' ? t(FIELD_LABELS[field].label) : t('{label} (opcional)', { label: t(FIELD_LABELS[field].label) })}
                  count={{ value, max: LIMITS[field] }}
                  error={textError(lang, field)}
                  hint={t(FIELD_LABELS[field].hint)}
                >
                  {(id) =>
                    field === 'intro' ? (
                      <textarea id={id} rows={4} lang={lang} {...props} />
                    ) : (
                      <input id={id} lang={lang} {...props} />
                    )
                  }
                </Field>
              );
            })}
          </div>
        </section>
      </div>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={() => void save()} onReset={reset} />
    </>
  );
}

/** Cómo se ve la foto en el sitio: la tarjeta (carrusel y páginas de noticias) y al compartir el enlace. */
function NewsPreviews({ media, crop }: { media: Media; crop: { x: number; y: number } }) {
  const { t } = useT();
  return (
    <div className="news-previews">
      <figure>
        <div className="pv pv-card">
          <img src={media.url} alt="" style={{ objectPosition: objectPosition(crop) }} />
        </div>
        <figcaption>{t('Tarjeta: carrusel y páginas de noticias')}</figcaption>
      </figure>
      <figure>
        <div className="pv pv-og">
          <div className="pv-og-inner">
            <img src={media.url} alt="" style={{ objectPosition: objectPosition(crop) }} />
          </div>
        </div>
        <figcaption>{t('Al compartir el enlace (1200 × 630)')}</figcaption>
      </figure>
    </div>
  );
}
